import React, { FC } from "react";
import styled from "styled-components";
import { ListItem, ListItemAvatar, ListItemText, List } from "@mui/material";

import InlineMessage from "@ingka/inline-message";
import SSRIcon from "@ingka/ssr-icon";
import documentUpload from "@ingka/ssr-icon/paths/document-upload";
import trashCan from "@ingka/ssr-icon/paths/trash-can";
import Tooltip from "@ingka/tooltip";
import Button from "@ingka/button";
import { SrOnly } from "../../styles";

const ListItemWrapper = styled(ListItem)`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	padding: 0 1rem;
	flex-wrap: wrap;
`;

const MessageWrapper = styled.div`
	padding: 0 1rem;
`;

const ActionButton = styled(Button)`
	margin-left: auto;
`;

export interface ApplicationListItemModel {
	id: string;
	title: string;
	isLoading: boolean;
	onDelete: () => void;
}

interface ApplicationListProps {
	items: ApplicationListItemModel[];
	emptyTitle?: string;
	emptyBody?: string;
	listId: string;
}

interface ApplicationListItemProps {
	item: ApplicationListItemModel;
}

export const ApplicationListItem: FC<ApplicationListItemProps> = ({ item }) => {
	const ariaLabel = `Cancel request to access ${item.title}`;

	return (
		<>
			<ListItemWrapper
				secondaryAction={
					<Tooltip interactiveCustomTrigger tooltipText={ariaLabel}>
						<ActionButton
							id="cancel-application-request-button"
							iconOnly
							size="small"
							text={ariaLabel}
							ssrIcon={trashCan}
							type="tertiary"
							aria-label={ariaLabel}
							onClick={item.onDelete}
							loading={item.isLoading}
						/>
					</Tooltip>
				}
			>
				<SrOnly id="loading-message" role="status" aria-live="polite" aria-atomic="true">
					{item.isLoading && "Loading"}
				</SrOnly>
				<ListItemAvatar>
					<SSRIcon aria-hidden paths={documentUpload} />
				</ListItemAvatar>

				<ListItemText primary={item.title} />
			</ListItemWrapper>
		</>
	);
};

export const ApplicationList: React.FC<ApplicationListProps> = ({ items, emptyTitle, emptyBody, listId }) => {
	if (!items.length && emptyBody && emptyTitle) {
		return (
			<MessageWrapper>
				<InlineMessage headingLevel="2" title={emptyTitle} body={emptyBody} />
			</MessageWrapper>
		);
	}

	return (
		<List id={listId}>
			{items.map((item) => (
				<ApplicationListItem key={item.id} item={item} />
			))}
		</List>
	);
};
